import React from 'react';
import { Divider } from 'antd';
import styled from 'styled-components';

import { FacetFilterInput, FacetMetadata, SearchResults as SearchResultType } from '../../../../../../types.generated';
import { EntityAndType } from '../../../types';
import { UnionType } from '../../../../../search/utils/constants';
import { SearchFiltersSection } from '../../../../../search/SearchFiltersSection';
import { EntitySearchResults, EntityActionProps } from './EntitySearchResults';
import MatchingViewsLabel from './MatchingViewsLabel';
import { ComponentSpinner } from '../../../../../shared/ComponentSpinner';
import { PaginationBar } from '../../../tabs/Glossary/PaginationBar';

const SearchBody = styled.div`
    height: 100%;
    overflow-y: auto;
    display: flex;
`;

const FiltersContainer = styled.div`
    display: flex;
    min-height: 100%;
    max-width: 260px;
    min-width: 260px;
    flex-direction: column;
    background-color: #ffffff;
    border-right: 1px solid;
    border-color: ${(props) => props.theme.styles['border-color-base']};

    @media (max-width: 992px) {
        max-width: 100%;
    }
`;

const ResultContainer = styled.div`
    height: auto;
    overflow: auto;
    flex: 1;
`;

interface Props {
    page: number;
    searchResponse?: SearchResultType | null;
    filters?: Array<FacetMetadata> | null;
    allFilters?: Array<FacetMetadata> | null;
    selectedFilters: Array<FacetFilterInput>;
    loading: boolean;
    showFilters?: boolean;
    unionType: UnionType;
    showLessPaginationItems?: boolean;
    onChangeFilters: (filters: Array<FacetFilterInput>) => void;
    onChangePage: (page: number) => void;
    onChangeUnionType: (unionType: UnionType) => void;
    isSelectMode: boolean;
    selectedEntities: EntityAndType[];
    setSelectedEntities: (entities: EntityAndType[]) => any;
    numResultsPerPage: number;
    setNumResultsPerPage: (numResults: number) => void;
    entityAction?: React.FC<EntityActionProps>;
    applyView?: boolean;
}

export const EmbeddedListSearchResults = ({
    page,
    searchResponse,
    filters,
    allFilters,
    selectedFilters,
    loading,
    showFilters,
    unionType,
    onChangeUnionType,
    onChangeFilters,
    onChangePage,
    isSelectMode,
    selectedEntities,
    setSelectedEntities,
    numResultsPerPage,
    setNumResultsPerPage,
    entityAction,
    applyView,
    showLessPaginationItems,
}: Props) => {
    const totalResults = searchResponse?.total || 0;

    return (
        <>
            <SearchBody className="f-flex-column-xs">
                {!!showFilters && (
                    <FiltersContainer>
                        <SearchFiltersSection
                            filters={filters}
                            allFilters={allFilters || []}
                            selectedFilters={selectedFilters}
                            unionType={unionType}
                            loading={loading}
                            onChangeFilters={onChangeFilters}
                            onChangeUnionType={onChangeUnionType}
                        />
                    </FiltersContainer>
                )}
                <ResultContainer>
                    {loading && <ComponentSpinner />}
                    {!loading && (
                        <EntitySearchResults
                            searchResults={searchResponse?.searchResults || []}
                            additionalPropertiesList={
                                searchResponse?.searchResults?.map((searchResult) => ({
                                    // when we add impact analysis, we will want to pipe the path to each element to the result this
                                    // eslint-disable-next-line @typescript-eslint/dot-notation
                                    degree: searchResult['degree'],
                                    // eslint-disable-next-line @typescript-eslint/dot-notation
                                    paths: searchResult['paths'],
                                })) || []
                            }
                            isSelectMode={isSelectMode}
                            selectedEntities={selectedEntities}
                            setSelectedEntities={setSelectedEntities}
                            bordered={false}
                            entityAction={entityAction}
                        />
                    )}
                </ResultContainer>
            </SearchBody>
            {!loading && <Divider className="ma-0" />}
            <PaginationBar
                page={page}
                loading={loading}
                totalCount={totalResults}
                pageSize={numResultsPerPage}
                showLessItems={showLessPaginationItems}
                onChangePage={(newPage) => onChangePage(newPage)}
                onShowSizeChange={setNumResultsPerPage}
            />
            {applyView ? <MatchingViewsLabel /> : <span />}
        </>
    );
};
